import type { UIMatch } from 'react-router';
import type { RouteHandle } from '@/routes/Router';

import { useMatches } from 'react-router';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { CoreWrapper } from '@/components/wrapper/CoreWrapper';
import { ToastProvider } from '../../../components/Toast';
import { routes } from '../../../constants/Constants';
import { useUrlChecker } from '../../../hooks/useUrlChecker';
import ProfileDrawer from '../../ProfileDrawer';
import { SideMenuV2 as SideMenu } from '../../SideMenu';
import SiteFooter from '../../SiteFooter';
import SiteFooterPublic from '../../SiteFooterPublic';
import { SiteHeader } from '../../SiteHeader/V2';
import { LayoutProvider, useLayoutContext } from '../../LayoutContext';

type Props = {
  children: JSX.Element[] | JSX.Element;
};

const PageWrapperContent = ({ children }: Props) => {
  const { isPublic } = useUrlChecker();
  const preRegister = isPublic || window.location.pathname === routes.unitsOwnedEnroll;
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
  const currentRoute = matches[matches.length - 1];
  const routeIsSplitView = currentRoute?.handle?.layout === 'split-view';
  const hideFooter = currentRoute?.handle?.hideFooter === true;

  const { isSplitView: contextIsSplitView } = useLayoutContext();
  const isSplitView = routeIsSplitView || contextIsSplitView;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {preRegister ? null : <SideMenu />}
      <Box
        id="page-container"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <SiteHeader />
        <Box
          sx={{
            flex: 1,
            px: { xs: 2, md: isPublic ? 0 : 4 },
          }}
        >
          <Box sx={{ margin: { xl: '0 auto' } }}>{children}</Box>
        </Box>
        {!hideFooter &&
          (isPublic ? <SiteFooterPublic /> : <SiteFooter isSplitView={isSplitView} />)}
        <ProfileDrawer />
      </Box>
    </Box>
  );
};

const PageWrapper = ({ children }: Props) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CoreWrapper>
        <ToastProvider>
          <LayoutProvider>
            <PageWrapperContent>{children}</PageWrapperContent>
          </LayoutProvider>
        </ToastProvider>
      </CoreWrapper>
    </LocalizationProvider>
  );
};

export default PageWrapper;
